const SLUG_REGEX = /^[a-z0-9-]+$/;

export interface EmbedOptions {
  slug: string;
  scriptUrl: string;
  /** Skip http/https validation of `scriptUrl`. The admin builds its snippet
   * from its own `window.location.origin`, a value it controls, so the URL is
   * trusted. Defaults to `false` (validate) — operator-supplied URLs must pass.
   * This is the ONE legitimate per-surface difference, expressed as a parameter
   * rather than a fork (ADR-0009). */
  trustScriptUrl?: boolean;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Build the copy-pasteable, self-configuring embed snippet. The widget resolves
 * its API base from its own <script src> origin, and the tenant is resolved
 * server-side from the slug, so the snippet carries neither.
 *
 * This is the single shared builder for both the `web` engine and the `admin`
 * UI — the XSS-escaping + slug-validation surface for operator-pasted embed
 * codes lives here once (ADR-0009). */
export function buildEmbedSnippet({ slug, scriptUrl, trustScriptUrl = false }: EmbedOptions): string {
  if (!SLUG_REGEX.test(slug)) {
    throw new Error(`invalid slug: ${JSON.stringify(slug)} (expected ${SLUG_REGEX})`);
  }
  if (!trustScriptUrl) {
    let parsed: URL;
    try {
      parsed = new URL(scriptUrl);
    } catch {
      throw new Error(`invalid scriptUrl: ${JSON.stringify(scriptUrl)} (not a URL)`);
    }
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      throw new Error(`invalid scriptUrl: ${JSON.stringify(scriptUrl)} (expected http/https)`);
    }
  }
  return [
    `<div data-quiz="${escapeAttr(slug)}"></div>`,
    `<script src="${escapeAttr(scriptUrl)}" async></script>`,
  ].join("\n");
}
